import styled, { css } from 'styled-components'

import { CrossSVG } from '@src/components/SVGS/CrossSVG'
import { SearchIconSVG } from '@src/components/SVGS/SearchIconSVG'
import { breakpoints } from '@src/constants/breakpoints'

export const StyledSearchInput = styled.input<{
  $showSearchBar: boolean
  $startAnimation: boolean
  $isMenuView: boolean
  $isStackedMenu?: boolean
}>`
  border-radius: 44px;
  position: absolute;

  ${({ $isMenuView }) =>
    $isMenuView
      ? css`
          width: 40px;
          height: 40px;
          right: 8px;
          bottom: 9px;
          border: 2px solid rgba(0, 0, 0, 0.1);
          &:focus {
            border: 2px solid #000;
            outline: none;
          }
        `
      : css`
          width: 48px;
          height: 48px;
          right: 64px;
          border: 2px solid ${({ theme }) => theme.colors.darkGrey};
          opacity: 1;
          outline: none;
          font-weight: 700;
          font-size: 16px;
          color: ${({ theme }) => theme.colors.darkText};
          background-color: white;

          &::placeholder {
            opacity: 0.3;
            color: ${({ theme }) => theme.colors.darkText};
          }

          &:hover:not(:focus) {
            color: ${({ theme }) => theme.colors.brand};
            &::placeholder {
              color: ${({ theme }) => theme.colors.brand};
            }
          }

          &:focus {
            border: 2px solid ${({ theme }) => theme.colors.darkText};
            &::placeholder {
              color: ${({ theme }) => theme.colors.darkText};
            }
          }
        `};

  box-sizing: border-box;

  @media (min-width: ${breakpoints.tabletMisc}px) {
    display: flex;
    position: static;
    width: calc(100% - 12px);
    border-radius: 24px;
    padding: 0px;
    padding-left: 42px;
    padding-right: 40px;
    margin: 0px;
    font-size: 16px;
  }

  @media (min-width: ${breakpoints.largeTablet}px) {
    display: flex;
    position: static;
    width: calc(100% - 40px);
    border-radius: 24px;
    box-sizing: content-box;
    padding-left: 40px;
    margin: 0px;
    width: 100%;
    font-size: 16px;
    ${({ $isMenuView }) =>
      $isMenuView
        ? css`
            height: 38px;
            padding-right: 40px;
          `
        : css`
            height: 44px;
            min-width: 300px;
            padding-right: 100px;
          `};
  }

  ${({ $showSearchBar, $isStackedMenu }) =>
    $showSearchBar &&
    css`
      animation: expand 0.2s;
      border: 2px solid ${$isStackedMenu ? 'rgba(0, 0, 0, 0.1)' : '#2e3333'};
      border-radius: 24px;
      position: absolute;
      right: 0;
      width: calc(100% - 18px);
      font-size: 15px;
      padding-left: 40px;
      margin: 0 8px;
      opacity: 1;
      background-color: #fff;
    `}

  ${({ $startAnimation }) =>
    $startAnimation &&
    css`
      animation: collapse 0.2s;
    `}

  @keyframes expand {
    from {
      width: 16px;
      margin: 0;
      border-right: transparent;
      border-radius: 24px 0px 0px 24px;
      opacity: 0;
      background-color: #f6f6f9;
    }
    to {
      width: calc(100% - 16px);
      margin: 0 8px;
      border-radius: 24px;
      opacity: 1;
      background-color: #fff;
    }
  }

  @keyframes collapse {
    from {
      width: calc(100% - 120px);
      margin: 0 16px;
      margin-right: 60px;
      border-radius: 24px;
      opacity: 1;
      background-color: #fff;
    }
    to {
      width: 48px;
      margin: 0;
      border-radius: 24px;
      opacity: 1;
      background-color: #fff;
    }
  }
`

export const MobileCategoryCircle = styled.div<{ $showSearchBar: boolean }>`
  border-radius: 44px;
  width: 48px;
  height: 48px;
  box-sizing: border-box;
  opacity: 1;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  background-color: white;
  border: 2px solid ${({ theme }) => theme.colors.darkGrey};
  &:focus {
    border: 2px solid ${({ theme }) => theme.colors.darkGrey};
  }
  &:hover {
    background-color: white;
  }

  display: ${({ $showSearchBar }) => $showSearchBar && 'none'};
`

export const Divider = styled.div<{ showOnRight?: boolean }>`
  width: 2px;
  height: 40px;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.darkGrey};
  top: -1px;
  left: -2px;
  z-index: 200;
  opacity: 0.2;

  @media (min-width: ${breakpoints.tabletMisc}px) {
    top: 12px;
    width: 3px;
    height: 40px;
    left: calc(100% - 58px);
  }

  @media (min-width: ${breakpoints.largeTablet}px) {
    top: 20px;
    height: 40px;
    left: calc(100% - 80px);
  }
`

export const StyledIconButton = styled.button<{
  $display: boolean
  $startAnimation: boolean
  $isMenuView: boolean
}>`
  ${({ $isMenuView }) =>
    $isMenuView
      ? css`
          width: 28px;
          height: 28px;
          display: none;
        `
      : css`
          width: 32px;
          height: 32px;
          left: 110%;
          top: 17px;
        `};

  background-color: ${({ theme }) => theme.colors.grey};
  position: absolute;
  border: none;
  border-radius: 24px;
  opacity: 0;
  z-index: 2;
  cursor: pointer;

  @media (min-width: ${breakpoints.tabletMisc}px) {
    top: 24px;
    left: calc(100% - 118px);
  }

  @media (min-width: ${breakpoints.largeTablet}px) {
    left: calc(100% - 118px);
    top: 24px;
  }

  ${({ $display, $isMenuView }) =>
    $display &&
    css`
      animation: slideInAndShow 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
      ${$isMenuView
        ? 'left: calc(100% - 44px); bottom: 15px;'
        : 'left: calc(100% - 96px);'};

      @media (min-width: ${breakpoints.tabletMisc}px) {
        animation: none;
        left: calc(100% - 96px);
        top: 16px;
      }

      @media (min-width: ${breakpoints.largeTablet}px) {
        animation: none;
        left: calc(100% - 118px);
        top: 24px;
      }
    `};

  ${({ $startAnimation }) =>
    $startAnimation &&
    css`
      animation: slideOutAndDisappear 0.3s;
    `}

  @keyframes slideInAndShow {
    from {
      opacity: 0;
      left: ${({ $isMenuView }) =>
        $isMenuView ? 'calc(100% - 80px)' : 'calc(100% - 96px)'};
    }
    to {
      opacity: 1;
      left: ${({ $isMenuView }) =>
        $isMenuView ? 'calc(100% - 44px)' : 'calc(100% - 96px)'};
    }
  }

  @keyframes slideOutAndDisappear {
    from {
      opacity: 1;
      left: ${({ $isMenuView }) =>
        $isMenuView ? 'calc(100% - 44px)' : 'calc(100% - 96px)'};
    }
    to {
      opacity: 0;
      left: ${({ $isMenuView }) =>
        $isMenuView ? 'calc(100% - 80px)' : 'calc(100% - 120px)'};
    }
  }

  &:hover {
    opacity: 0.7;
  }
`

export const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
`

export const StyledMenuCross = styled.button<{
  displayPersistentBasket: boolean
}>`
  position: absolute;
  display: none;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  z-index: 10;

  @media (min-width: ${breakpoints.tablet}px) {
    display: flex;
    width: 28px;
    height: 28px;
    border-radius: 24px;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
  }

  @media (min-width: ${breakpoints.largeTablet}px) {
    width: 32px;
    height: 32px;
    right: 6px;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const StyledCross = styled(CrossSVG)`
  ${({ theme }) => theme.colors.brand}
  align-self: center;

  @media (min-width: ${breakpoints.largeTablet}px) {
    left: 0;
    z-index: 10;
  }
`

export const StyledSearchBarIcon = styled(SearchIconSVG)<{
  $showSearchBar: boolean
  $startAnimation: boolean
  $isFocused: boolean
  $isMenuView: boolean
}>`
  position: absolute;
  ${({ $isMenuView }) =>
    $isMenuView
      ? css`
          left: calc(100% - 38px);
          bottom: 18px;
        `
      : css`
          left: calc(100% - 98px);
          top: 22px;
        `};
  height: 20px;
  width: 20px;

  color: ${({ theme, $isFocused, $isMenuView }) =>
    $isFocused || !$isMenuView ? theme.colors.darkText : '#a0a4b6'};

  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    height: 20px;
    width: 20px;
    top: ${({ $isMenuView }) => !$isMenuView && '22px'};
  }

  @media (min-width: ${breakpoints.tabletMisc}px) {
    position: relative;
    left: 36px;
    height: 20px;
    width: 20px;
    top: ${({ $isMenuView }) => ($isMenuView ? '10px' : '14px')};
  }

  @media (min-width: ${breakpoints.largeTablet}px) {
    position: relative;
    left: 36px;
    height: 30px;
    width: 30px;
    top: ${({ $isMenuView }) => ($isMenuView ? '6px' : '9px')};
  }

  ${({ $showSearchBar, $isMenuView }) =>
    $showSearchBar &&
    css`
      animation: moveAndShrink 0.2s;
      left: 24px;
      top: ${!$isMenuView && '12px'};
      @media (min-width: ${breakpoints.tabletMisc}px) {
        animation: none;
      }
    `}

  ${({ $startAnimation }) =>
    $startAnimation &&
    css`
      animation: moveAndExpand 0.2s;
    `}

  @keyframes moveAndShrink {
    from {
      left: calc(100% - 64px);
      height: 20px;
      width: 20px;
    }
    to {
      left: 22px;
    }
  }

  @keyframes moveAndExpand {
    from {
      left: ${({ $isMenuView }) => ($isMenuView ? '84px' : '24px')};
    }
    to {
      left: ${({ $isMenuView }) =>
        $isMenuView ? 'calc(100% - 38px);' : 'calc(100% - 98px);'};
    }
  }
`

export const CategoryIconButton = styled.button<{
  $showSearchBar: boolean
  $startAnimation: boolean
}>`
  width: 40px;
  height: 40px;
  margin-top: 4px;
  background-color: transparent;
  border: transparent;
  cursor: pointer;
  z-index: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
  }

  ${({ $showSearchBar }) =>
    $showSearchBar &&
    css`
      animation: slideIn 1s;
      right: 0px;
      margin-right: 14px;
      background-color: transparent;
      top: 10px;
      position: absolute;

      @media (min-width: ${breakpoints.tablet}px) {
        margin-right: 14px;
        animation: none;
        top: 14px;
        margin-top: 0px;
      }

      @media (min-width: ${breakpoints.largeTablet}px) {
        margin-right: 7px;
        top: 22px;
        padding-right: 54px;
        margin-top: 0px;
      }

      @keyframes slideIn {
        from {
          right: -10px;
          opacity: 0;
        }
        to {
          right: 0px;
          opacity: 1;
        }
      }
    `}
`

export const RefContainer = styled.div`
  width: 75%;
  display: flex;
`

export const SearchInputContainer = styled.div<{
  isFocused: boolean
  $isMenuView: boolean
}>`
  display: flex;
  margin-right: 10px;
  justify-self: flex-end;
  align-self: center;

  ${({ $isMenuView, isFocused }) =>
    $isMenuView
      ? css`
          min-width: ${isFocused ? '350px' : '200px'};
        `
      : css`
          min-width: 350px;
          width: 100%;
          &:hover:not(:has(${StyledSearchInput}:focus)) {
            ${StyledSearchBarIcon} {
              color: ${({ theme }) => theme.colors.brand};
            }
            ${CategoryIconButton} {
              color: ${({ theme }) => theme.colors.brand};
            }
          }
        `}

  @media (min-width: ${breakpoints.largeTablet}px) {
    margin: 0;
  }
`
export const MobileOutletMenuSearchContainer = styled.div`
  display: flex;
  flex-direction: row;
`
